import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateDialogChip, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { format } from 'date-fns';
import { mapToContactMinDto } from 'pages/contacts/util';
import { useCallback, useMemo } from 'react';
import { useFindMatterRatesQuery, useGetMatterByIdQuery } from 'store/api/matters';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import {
    EngagementLengthUnit,
    MatterDto,
    MatterForm,
    MatterPartyRoleType,
    MatterStateType,
    OfferValueType,
    RemunerationCalcType,
} from 'types/matters';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierOptionByCode, getOptionsFromClassifier } from 'utils/formUtil';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { toTimecardIncrementOption } from 'utils/timecardIncrementOptions';
import { useClassifiers } from 'utils/useClassifiers';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import MatterPageForm from './MatterPageForm';
import { copiedPayerRows } from './matterPayers';
import { copiedPricingLink, seedCopiedPricing } from './matterPricingCopy';
import { copiedPracticeAreas, reduceParties, splitCaseStaff } from './matterUtils';
import { useInactivePricedPeople } from './useInactivePricedPeople';
import { useSettings } from 'utils/useSettings';
import { Setting } from 'types/back-office-settings';

const USED_CLASSIFIERS = [
    'ACCOUNTING_REVENUE_TYPE',
    'ARCHIVING_PROPERTIES_TYPE',
    'MATTER_STATE_TYPE',
    'MATTER_TYPE',
    'MATTER_BLOCKING_POLICY_TYPE',
    'REASON_OFFER_LOST',
    'OFFER_VALUE_TYPE',
    'ENGAGEMENT_LENGTH_UNIT',
    'LANGUAGE',
    'CURRENCY',
    'PRICING_LIMITATION_TYPE',
    'CAP_UNIT_TYPE',
    'INVOICE_TEMPLATE_TYPE',
    'INVOICING_INTERVAL_TYPE',
    'JOB_ROLE_GROUP_TYPE',
    'CONTRACT_STATUS_TYPE',
];

const MatterPageCopy = () => {
    const { id: originalMatterId } = useCreateRoute().params;

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { data: originalMatter, isError, isLoading } = useGetMatterByIdQuery(originalMatterId ?? skipToken);
    useCreateDialogChip({ copyOf: originalMatter?.caseNumber });
    // A copy takes the source's pricing along as staged tabs: the list in force on the new opening date becomes
    // the base tab, later lists their own tabs, and lists that already ended are left behind.
    const { data: sourcePricelists, isLoading: isLoadingPricelists } = useFindMatterRatesQuery(
        originalMatterId ?? skipToken
    );
    const { inactiveEmployeeIds, isLoading: isLoadingActivePricedEmployees } =
        useInactivePricedPeople(sourcePricelists);

    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.MATTER,
    });

    const isOfficialRefNumberEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFICIAL_REF_NUMBER_FIELD);
    const isAdministratorFieldEnabled = useSettings().isSettingEnabled(Setting.ADMINISTRATOR_FIELD);
    const canManagePayers = useHasPermissions(['UPDATE.MATTERS.ADD_AND_MANAGE_PAYERS']);
    const isSinglePracticeArea = useIsFeatureEnabled(FeatureFlag.CASE_PA_FIELD_SINGLE_SELECTION_ONLY);

    const getInitialDataForCopy = useCallback(
        (matter: MatterDto | undefined): MatterForm | undefined => {
            if (matter && classifiers) {
                const copyOpenedDate = format(new Date(), DEFAULT_DATE_FORMAT);
                return {
                    readOnlyCaseNumber: null,
                    name: matter.name,
                    clientParty: matter.clientParty ? mapToContactMinDto(matter.clientParty) : null,
                    matterStateType:
                        (
                            getOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === MatterStateType.IN_PROGRESS) ?? null,
                    matterType: getClassifierOptionByCode<string, string>(
                        classifiers['MATTER_TYPE'],
                        matter.matterTypeCode ?? null
                    ),
                    // The offer estimates belong to the original offer, so the copy starts from the defaults.
                    offerValueType:
                        (
                            getOptionsFromClassifier(classifiers['OFFER_VALUE_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === OfferValueType.CASH) ?? null,
                    offerValueLow: '',
                    offerValueHigh: '',
                    estimatedEngagementLength: '',
                    estimatedEngagementUnit:
                        (
                            getOptionsFromClassifier(classifiers['ENGAGEMENT_LENGTH_UNIT']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === EngagementLengthUnit.WEEKS) ?? null,
                    // An Offer Won case can only have one case opened from it, so the copy must not inherit the link.
                    originatesFromMatterId: null,
                    copiedFromMatterId: matter.id,
                    handledByOffice: matter.handledByOffice ? { ...matter.handledByOffice } : null,
                    generalNotes: matter.generalNotes ?? '',
                    officialReference: isOfficialRefNumberEnabled ? (matter.officialReference ?? '') : '',
                    isUsedTimeUsedForRemuneration:
                        matter.remunerationCalcTypeCode === RemunerationCalcType.EQUALS_USED_TIME,
                    isConfidential: matter.isConfidential ?? false,
                    description: matter.clientParty ? [matter.clientParty.name, matter.name].join(', ') : '',
                    ...copiedPracticeAreas(matter, isSinglePracticeArea),
                    serviceAgreementContract: matter.serviceAgreementContract || null,
                    timecardIncrement:
                        matter.timecardIncrement != null ? toTimecardIncrementOption(matter.timecardIncrement) : null,
                    project: matter.project ?? null,
                    openedDate: copyOpenedDate,
                    closedDate: null,
                    responsibleEmployee: matter.responsibleEmployee ?? null,
                    supervisedByEmployee: matter.supervisedByEmployee ?? null,
                    ...splitCaseStaff(matter.employees ?? [], isAdministratorFieldEnabled),
                    primaryContactParty: matter.primaryContactParty
                        ? { id: matter.primaryContactParty.id, name: matter.primaryContactParty.name }
                        : null,
                    representedClients: reduceParties(matter.parties ?? [], MatterPartyRoleType.CLIENT_REPRESENTED),
                    isNoOpponent: matter.isNoOpponent ?? false,
                    opponentParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.OPPONENT),
                    targetParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.TARGET),
                    otherParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.OTHER_PARTY),
                    judgeParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.JUDGE),
                    archivedInOffice: null,
                    archivedDate: null,
                    archivingNotes: '',
                    archivingPropertiesType: null,
                    discountPct: matter.discountPct ?? '',
                    discountExpiryDate: matter.discountExpiryDate
                        ? formatDateStringInput(new Date(matter.discountExpiryDate))
                        : null,
                    isCostIncluded: matter.isCostIncluded ?? false,
                    pricingLimitationType:
                        (
                            getOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.pricingLimitationTypeCode) ?? null,
                    accountingRevenueType:
                        (
                            getOptionsFromClassifier(classifiers['ACCOUNTING_REVENUE_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.accountingRevenueTypeCode) ?? null,
                    capUnitType:
                        (
                            getOptionsFromClassifier(classifiers['CAP_UNIT_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.capUnitTypeCode) ?? null,
                    capLimit: matter.capLimit ?? '',
                    fixedFee: matter.fixedFee ?? '',
                    recurringFixedFee: matter.recurringFixedFee ?? '',
                    billingNotes: matter.billingNotes ?? '',
                    invoicingInfo: matter.invoicingInfo ?? '',
                    timecardLanguage:
                        (
                            getOptionsFromClassifier(classifiers['LANGUAGE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.timecardLanguageCode) ?? null,
                    primaryRecipientParty: matter.primaryRecipientParty
                        ? {
                              id: matter.primaryRecipientParty.id,
                              name: matter.primaryRecipientParty.name,
                              email: matter.primaryRecipientParty.mainEmail || '',
                          }
                        : null,
                    primaryRecipientAdditionalEmails: matter.primaryRecipientAdditionalEmails ?? '',
                    secondaryRecipientParties: (matter.parties || [])
                        .filter(
                            ({ matterPartyRoleTypeCode }) =>
                                matterPartyRoleTypeCode === MatterPartyRoleType.SECONDARY_RECIPIENT
                        )
                        .map(({ party }) => party)
                        .map(({ id, name, mainEmail }) => ({
                            id,
                            name,
                            email: mainEmail,
                        })),
                    secondaryRecipientAdditionalEmails: matter?.secondaryRecipientAdditionalEmails ?? '',
                    matterClientReference: matter?.matterClientReference ?? '',
                    invoiceMatterName: matter.name,
                    invoiceMatterNameLocalLanguage: '',
                    invoiceTemplate: matter.invoiceTemplate
                        ? { id: matter.invoiceTemplate.id, name: matter.invoiceTemplate.name }
                        : null,
                    invoicedByOffice: matter.invoicedByOffice ? { ...matter.invoicedByOffice } : null,
                    invoicingIntervalType:
                        (
                            getOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.invoicingIntervalTypeCode) ?? null,
                    payerParty: matter.payerParty,
                    payers: copiedPayerRows(matter, canManagePayers),
                    invoiceSpecTemplate: matter.invoiceSpecTemplate
                        ? { id: matter.invoiceSpecTemplate.id, name: matter.invoiceSpecTemplate.name }
                        : null,
                    referencedPricelist:
                        matter.pricelist || matter.pricingHash
                            ? { id: matter.pricelist?.id || null, pricingHash: matter.pricingHash }
                            : null,
                    pricelists: seedCopiedPricing(sourcePricelists ?? [], copyOpenedDate),
                    ...copiedPricingLink(matter),
                    pricelistCurrencyType:
                        (
                            getOptionsFromClassifier(classifiers['CURRENCY']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.pricelistCurrencyTypeCode) ?? null,
                    paymentCurrencyType:
                        (
                            getOptionsFromClassifier(classifiers['CURRENCY']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.paymentCurrencyTypeCode) ?? null,
                    isPaymentCurrencyDifferent:
                        !!matter.paymentCurrencyTypeCode &&
                        matter.paymentCurrencyTypeCode !== matter.pricelistCurrencyTypeCode,
                    isJointInvoicingDisabled: matter.isJointInvoiceDisabled ?? false,
                    isAutomaticClosingExempt: matter.isAutomaticClosingExempt ?? false,
                    matterBlockingPolicyType:
                        (
                            getOptionsFromClassifier(classifiers['MATTER_BLOCKING_POLICY_TYPE']) as {
                                label: string;
                                value: string;
                            }[]
                        ).find((val) => val.value === matter.matterBlockingPolicyTypeCode) ?? null,
                    nextInvoicingDate: null,
                    paymentNotificationReceivers:
                        matter.paymentNotificationReceivers?.map((item) => item.employee) ?? [],
                    dmsLink: null,
                    isAccessibleFirmWide: matter.isAccessibleFirmWide ?? false,
                    preserveNonBillableHours: matter.preserveNonBillableHours,
                    isExcludedFromXR: matter.isExcludedFromXR,
                    isPaperFile: matter.isPaperFile,
                    invoiceCustomization: matter.invoiceCustomization,
                    isSpecIncludedInInvoice: matter.isSpecIncludedInInvoice ?? false,
                    isSpecExcludedFromEmail: matter.isSpecExcludedFromEmail ?? false,
                    // A copy is a new case, so it starts unmarked whatever the source carries.
                    isAmlBlockingExempt: false,
                    changeReasons: null,
                    ...initialCustomFieldsObject,
                };
            }
            return undefined;
        },
        [
            initialCustomFieldsObject,
            classifiers,
            isOfficialRefNumberEnabled,
            isAdministratorFieldEnabled,
            isSinglePracticeArea,
            sourcePricelists,
            canManagePayers,
        ]
    );

    const initialData = useMemo(
        () => (isLoadingCustomFieldData ? undefined : getInitialDataForCopy(originalMatter)),
        [getInitialDataForCopy, originalMatter, isLoadingCustomFieldData]
    );

    if (
        isLoading ||
        isLoadingClassifiers ||
        isLoadingPricelists ||
        isLoadingActivePricedEmployees ||
        (initialData === undefined && !isError) ||
        isLoadingCustomFieldData
    ) {
        return <Loader />;
    }

    return (
        <MatterPageForm
            type="add"
            initialData={initialData}
            classifiers={classifiers}
            cancelRedirectPath="/matters"
            inactiveEmployeeIds={inactiveEmployeeIds}
        />
    );
};

export default MatterPageCopy;
